import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { HearFromSection } from 'stile-shared/src/components/2024/common/HearFromSection';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import { RealWorldSection } from 'stile-shared/src/components/2024/common/RealWorldSection';
import PageLinkFeature from 'stile-shared/src/components/2024/layout/PageLinkFeature';
import { TopSection } from 'stile-shared/src/components/2024/layout/RegionBasedTopSection';
import ReviewsSlider from 'stile-shared/src/components/2024/layout/ReviewsSlider/ReviewsSlider';
import { SideBySideSection } from 'stile-shared/src/components/2024/layout/SideBySideSection';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const LINKS = {
  guideDownload: '/resources/victorian-curriculum-2026/',
  emmaStory: '/why-choose-stile/case-studies/maryborough-education-centre/',
  department: 'https://arc.educationapps.vic.gov.au/learning/sites/vtlmresources',
  trialStile: '/set-up-trial/',
};

const exploreCards = [
  {
    title: 'Lessons',
    description:
      'Our customisable and interactive lessons are the perfect mix of print, hands-on and digital.',
    url: '/what-is-stile/inside-stiles-lessons/',
  },
  {
    title: 'Stile X Workbooks',
    description: 'Designed for hands-on learning and perfectly matched to the digital curriculum.',
    url: '/what-is-stile/stile-x/',
  },
  {
    title: 'Pedagogies',
    description: 'Weaving evidence-based strategies into every lesson.',
    url: '/pedagogies/5e-instructional-model/',
  },
];

const VicTopSection = styled(TopSection)`
  background-color: var(--white);
  .mobile-br {
    display: none;
    @media (max-width: 768px) {
      display: inline;
    }
  }
  .content {
    align-items: center;
    gap: 60px;
    /* desktop-only so the base TopSection mobile padding (28px/30px) still applies */
    @media (min-width: 769px) {
      padding-bottom: 120px;
      padding-top: 80px;
    }
    .--left {
      align-self: center;
      max-width: 498px;
      h1 {
        margin: 0px 0px 24px;
      }
    }
    .--right {
      display: flex;
      justify-content: flex-end;
      .gatsby-image-wrapper {
        margin-top: 0;
        width: 100%;
        max-width: 566px;
        border-radius: 32px;
        box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
          0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
      }
    }
  }
  @media (max-width: 768px) {
    .content {
      align-items: flex-start;
      /* 60px between the hero image and the section below, per Figma */
      padding-bottom: 60px;
      gap: 24px;
      .--left h1 {
        margin: 0px 0px 16px;
      }
      .--right {
        justify-content: center;
        .gatsby-image-wrapper {
          margin-top: 8px;
          margin-bottom: 0;
          border-radius: 20px;
        }
      }
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 150px 0 0;
  gap: 150px;
  @media (max-width: 768px) {
    margin: 60px 0 0;
    gap: 60px;
  }
`;

const VicSideBySideSection = styled(SideBySideSection)`
  p {
    margin: 12px 0;
  }
  .left-img {
    margin-top: 0;
  }
  .right-text a.button {
    margin-top: 20px;
  }
  h2 {
    margin-bottom: 32px;
    margin-top: 0;
  }
  @media (max-width: 768px) {
    .left-img {
      margin-top: 0;
    }
    h2 {
      margin-bottom: 28px;
    }
  }
`;

const StyledImageWrapper = styled.div`
  display: flex;
  min-width: 267px;
  width: 100%;
  justify-content: center;
  .gatsby-image-wrapper {
    width: 100%;
    max-width: 566px;
    border-radius: 32px;
    box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
      0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
    @media (max-width: 768px) {
      border-radius: 20px;
    }
  }
`;

function VicLandingPage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <VicTopSection>
        <div className="content">
          <div className="--left">
            <h1>
              Turning <br className="mobile-br" />
              VTLM 2.0 into everyday practice
            </h1>
            <p className="--feature_heading">
              Built for explicit teaching and specialising in science, Stile is aligned with every
              element of the Victorian Teaching and Learning Model.
            </p>
          </div>
          <div className="--right">
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/vic_vtlm_hero.jpg"
              alt="A science teacher leading a Stile lesson in a Victorian classroom"
              placeholder="none"
              loading="eager"
            />
          </div>
        </div>
      </VicTopSection>

      <ReviewsSlider />

      <Content>
        <VicSideBySideSection>
          <div className="image-left text-first-mobile content">
            <div className="left-img">
              <StyledImageWrapper>
                <StaticImage
                  src="../../../../stile-shared/assets/images/resources/vic_vtlm_guide_cover.jpg"
                  alt="The Victorian Teaching and Learning Model 2.0 — Stile's guide to implementation"
                  quality={100}
                />
              </StyledImageWrapper>
            </div>
            <div className="right-text">
              <h2>
                VTLM 2.0 is ready.
                <br />
                So are we.
              </h2>
              <p>
                Stile’s instructional model is underpinned by the same evidence base as the most
                recent Victorian Teaching and Learning Model. This means teaching with Stile is
                implementing VTLM 2.0.
              </p>
              <p>
                Download our complete guide to implementation, including a self-assessment to
                determine your priorities in preparing for the{' '}
                <Link url={LINKS.department} openInNewTab>
                  Department
                </Link>
                ’s “full mandatory embedding in teaching and learning programs across all schools”
                by the start of 2028.
              </p>
              <Button
                href={LINKS.guideDownload}
                label={
                  <>
                    <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                    <span>Download the guide</span>
                  </>
                }
              />
            </div>
          </div>
        </VicSideBySideSection>
      </Content>

      {/* TODO(nik): pass youtubeUrl once Emma's video is supplied. */}
      <HearFromSection
        textFirstMobile
        heading="How teachers are bringing VTLM 2.0 to life in the science classroom"
        subheading={
          <>
            <p>
              Stile is built to support science teachers in integrating evidence-based teaching
              practices into their classrooms.
            </p>
            <p>
              Emma Holgate, KLA Leader - Science at Maryborough Education Centre, talks about how
              Stile provides a backbone for their science teaching program with explicit instruction
              built in. This allows teachers to “put attention onto their students and building
              relationships, because they know the starting point is there.”
            </p>
          </>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/vic_emma_holgate.jpg"
            alt="Emma Holgate speaking about Stile in her science classroom"
          />
        }
        button={
          <Button
            href={LINKS.emmaStory}
            label={
              <>
                <span>Hear Emma’s story</span>
              </>
            }
          />
        }
      />

      <RealWorldSection
        className="--centered-mobile-header"
        heading={
          <h2>
            What is the <br className="mobile-br" />
            VTLM 2.0?
          </h2>
        }
        subheading="The VTLM 2.0 is a model of teaching and learning from the Victorian Department of Education. It aims to give teachers clarity around the most effective teaching strategies, emphasising those supported by evidence of how students learn. Victorian Government schools are expected to have implemented the model by 2028."
        tabs={['Planning', 'Enabling learning', 'Explicit teaching', 'Supported application']}
        headings={['Planning', 'Enabling learning', 'Explicit teaching', 'Supported application']}
        multiParagraphs={[
          [
            'This element of the model refers to a whole-school approach to planning, where common teaching and learning programs sequence what will be taught.',
            'Stile’s ready-made scope and sequence, teaching plans and lab guides remove the need to build a whole-school science curriculum from scratch. Having a high-quality starting point saves teachers hundreds of hours, and lets them focus on what they do best: teaching.',
          ],
          [
            'This element is about building positive relationships with students, being responsive to their cultures and setting expectations.',
            'Stile is designed to support a culturally responsive approach through the inclusion of contextualised real-world science, “take me home” activities that invite sharing of knowledge from students’ homes and communities, and representation of diverse and relatable role models.',
          ],
          [
            'This element is foundational to the VTLM 2.0. It refers to incorporating specific, evidence-based practices such as the use of learning objectives, giving clear explanations of new information, scaffolding the learning process, modelling, the use of assessment, and feedback.',
            'Stile has been built from the ground up as an explicit teaching tool. Every lesson includes clear learning objectives and accompanying Key Questions to measure student progress. The combination of videos, text, simulations and questions is crafted to support you in providing explicit explanations of new knowledge. Immediate visibility of student data allows you to be responsive to students’ needs on the spot.',
          ],
          [
            'Supported application refers to regularly revisiting and reviewing knowledge.',
            'Stile’s revision resources, including printed Stile X workbooks, are designed to have students applying proven strategies for organising new knowledge, consolidating their learning and applying their understanding.',
          ],
        ]}
        images={[
          <StaticImage
            key="planning"
            src="../../../../stile-shared/assets/images/resources/vic_vtlm_planning.jpg"
            alt="A grid of real-world Stile science lessons"
          />,
          <StaticImage
            key="enabling"
            src="../../../../stile-shared/assets/images/resources/vic_vtlm_enabling.jpg"
            alt="Illustrated, diverse and relatable science role models"
          />,
          <StaticImage
            key="explicit"
            src="../../../../stile-shared/assets/images/resources/vic_vtlm_explicit.jpg"
            alt="A teacher explicitly teaching a science concept to students"
          />,
          <StaticImage
            key="supported"
            src="../../../../stile-shared/assets/images/resources/vic_vtlm_supported.jpg"
            alt="A student completing a printed Stile X revision workbook"
          />,
        ]}
      />

      <PageLinkFeature
        mobileBackground="var(--stile-grey-1)"
        heading="See how Stile can support you in implementing the VTLM 2.0"
        copy={
          <div>
            <p>
              We believe in empowering teachers with the best support. In Victoria, that means
              teaming up with <strong>Patty Luna</strong>, a seasoned science educator and
              Melbourne-based curriculum specialist.
            </p>
            <p>
              With over two decades of experience, Patty is an expert in bringing science to life.
              Her background includes teaching across all sectors in the Victorian education system
              and leading professional learning with the Academy of Science.
            </p>
            <p>
              Patty is here to guide you through Stile and show you just how transformative it can
              be.
            </p>
          </div>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/patty_luna.png"
            alt="Patty Luna, Stile's Victoria-based curriculum specialist"
            quality={100}
          />
        }
        button={
          <Button
            href={LINKS.trialStile}
            label={<span>Learn more about trialling Stile at your school</span>}
          />
        }
      />

      <ExploreCurriculum
        title="Explore our core science curriculum"
        exploreCardsData={exploreCards}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/responsive-support/book-with-grad-cap.svg"
            alt="A book with a graduation cap"
          />
        }
      />
    </PageLayout>
  );
}

export default VicLandingPage;
